import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Users, 
  MapPin, 
  Calendar,
  Trophy,
  Table,
  Wallet,
  TrendingUp,
  Building2,
  Shield,
  ChevronRight,
  Plus,
  FileText
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }

    const fetchData = async () => {
      try {
        const [statsRes, reportsRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/reports'),
        ]);
        setStats(statsRes.data);
        setReports(reportsRes.data);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, navigate]);

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Skeleton className="h-8 w-64 mb-8" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  const statCards = [
    { label: 'Spieler', value: stats?.total_players || 0, icon: Users, color: 'text-blue-500', bg: 'bg-blue-500/10' },
    { label: 'Feldbesitzer', value: stats?.total_field_owners || 0, icon: Building2, color: 'text-green-500', bg: 'bg-green-500/10' },
    { label: 'Felder', value: stats?.total_fields || 0, icon: MapPin, color: 'text-purple-500', bg: 'bg-purple-500/10' },
    { label: 'Teams', value: stats?.total_teams || 0, icon: Users, color: 'text-teal-500', bg: 'bg-teal-500/10' },
    { label: 'Spiele', value: stats?.total_matches || 0, icon: Calendar, color: 'text-amber-500', bg: 'bg-amber-500/10' },
    { label: 'Turniere', value: stats?.total_tournaments || 0, icon: Trophy, color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
    { label: 'Ligen', value: stats?.total_leagues || 0, icon: Table, color: 'text-pink-500', bg: 'bg-pink-500/10' },
    { label: 'Einnahmen', value: formatCurrency(stats?.total_fees || 0), icon: Wallet, color: 'text-emerald-500', bg: 'bg-emerald-500/10', isText: true },
  ];

  const quickActions = [
    { label: 'Spieler verwalten', path: '/admin/players', icon: Users },
    { label: 'Feldbesitzer verwalten', path: '/admin/field-owners', icon: Building2 },
    { label: 'Teams ansehen', path: '/admin/teams', icon: Users },
    { label: 'Turniere verwalten', path: '/admin/tournaments', icon: Trophy },
    { label: 'Ligen verwalten', path: '/admin/leagues', icon: Table },
    { label: 'Reports', path: '/admin/reports', icon: FileText },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 bg-gradient-to-br from-tunis-blue to-tunis-teal rounded-2xl flex items-center justify-center">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-tunis-blue">
              Admin Dashboard
            </h1>
            <p className="text-muted-foreground">Plattform-Übersicht und Verwaltung</p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {statCards.map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <Card key={idx} className="border-slate-200" data-testid={`stat-${stat.label}`}>
                <CardContent className="p-4">
                  <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center mb-3`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div className="font-display font-bold text-2xl text-tunis-blue">
                    {stat.isText ? stat.value : stat.value.toLocaleString()}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Weekly Stats Banner */}
        <Card className="bg-gradient-to-r from-tunis-blue to-tunis-teal text-white border-0 mb-8">
          <CardContent className="p-4 sm:p-6">
            <h3 className="font-bold text-base sm:text-lg mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Diese Woche
            </h3>
            <div className="grid grid-cols-3 gap-2 sm:gap-6">
              <div className="text-center sm:text-left">
                <div className="text-xl sm:text-3xl font-bold">{reports?.weekly_stats?.new_users || 0}</div>
                <div className="text-xs sm:text-sm text-white/80">Neue Benutzer</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-xl sm:text-3xl font-bold">{reports?.weekly_stats?.new_matches || 0}</div>
                <div className="text-xs sm:text-sm text-white/80">Neue Spiele</div>
              </div>
              <div className="text-center sm:text-left">
                <div className="text-xl sm:text-3xl font-bold">{reports?.weekly_stats?.new_teams || 0}</div>
                <div className="text-xs sm:text-sm text-white/80">Neue Teams</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <h2 className="font-display font-bold text-xl text-tunis-blue mb-4">Schnellzugriff</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
          {quickActions.map((action, idx) => {
            const Icon = action.icon;
            return (
              <Link key={idx} to={action.path}>
                <Card className="border-slate-200 hover:shadow-card-hover hover:-translate-y-1 transition-all cursor-pointer h-full">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-tunis-blue/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Icon className="w-6 h-6 text-tunis-blue" />
                    </div>
                    <div className="text-sm font-medium text-tunis-blue">{action.label}</div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>

        {/* Recent Activity */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Top Players */}
          <Card className="border-slate-200" data-testid="top-players">
            <CardHeader>
              <CardTitle className="text-tunis-blue flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Top Spieler
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {reports?.top_players?.slice(0, 5).map((player, idx) => (
                  <div key={player.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-8 h-8 bg-tunis-blue text-white rounded-full flex items-center justify-center font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-tunis-blue">{player.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {player.matches_played} Spiele • {player.wins} Siege
                      </div>
                    </div>
                    <Badge className="bg-amber-500/10 text-amber-600">
                      {player.elo_points} ELO
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Top Teams */}
          <Card className="border-slate-200" data-testid="top-teams">
            <CardHeader>
              <CardTitle className="text-tunis-blue flex items-center gap-2">
                <Users className="w-5 h-5 text-tunis-teal" />
                Top Teams
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {reports?.top_teams?.slice(0, 5).map((team, idx) => (
                  <div key={team.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl">
                    <div className="w-8 h-8 bg-tunis-teal text-white rounded-full flex items-center justify-center font-bold">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-tunis-blue">{team.name}</div>
                      <div className="text-sm text-muted-foreground">
                        {team.member_count} Mitglieder • {team.wins} Siege
                      </div>
                    </div>
                    <Badge className="bg-tunis-teal/10 text-tunis-teal">
                      {team.team_elo} ELO
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Transactions */}
        <Card className="border-slate-200 mt-6" data-testid="recent-transactions">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-tunis-blue flex items-center gap-2">
              <Wallet className="w-5 h-5 text-green-500" />
              Letzte Transaktionen
            </CardTitle>
            <Link to="/admin/reports">
              <Button variant="ghost" size="sm">
                Alle ansehen
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {reports?.recent_transactions?.slice(0, 5).map((tx) => (
                <div key={tx.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                  <div>
                    <div className="font-medium text-tunis-blue text-sm">{tx.description}</div>
                    <div className="text-xs text-muted-foreground">{tx.type}</div>
                  </div>
                  <div className={`font-bold ${tx.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {tx.amount >= 0 ? '+' : ''}{formatCurrency(tx.amount)}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
